import React from 'react'

interface OptimizedImageProps {
  /** Base name of the image without extension */
  imageName: string
  /** Alt text for accessibility */
  alt: string
  /** CSS classes to apply */
  className?: string
  /** Width in pixels */
  width?: number
  /** Height in pixels */
  height?: number
  /** Loading strategy */
  loading?: 'lazy' | 'eager'
  /** Priority for LCP images */
  priority?: boolean
}

/**
 * Optimized image component that provides WebP support with JPEG fallback
 * Automatically generates responsive image sources for different screen sizes
 */
export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  imageName,
  alt,
  className = '',
  width,
  height,
  loading = 'lazy',
  priority = false,
}) => {
  // Base path for optimized images
  const basePath = '/assets/images/optimized'

  // Generate responsive image sources
  const generateSrcSet = (extension: 'webp' | 'jpg') => {
    const sizes = [
      { width: 400, suffix: '400x267' },
      { width: 800, suffix: '800x533' },
      { width: 1200, suffix: '1200x800' },
    ]

    return sizes
      .map(size => `${basePath}/${imageName}_${size.suffix}.${extension} ${size.width}w`)
      .join(', ')
  }

  // Generate sizes attribute for responsive behavior
  const sizes = '(max-width: 480px) 400px, (max-width: 768px) 800px, 1200px'

  return (
    <picture>
      {/* WebP format for modern browsers */}
      <source type="image/webp" srcSet={generateSrcSet('webp')} sizes={sizes} />

      {/* JPEG fallback for older browsers */}
      <source type="image/jpeg" srcSet={generateSrcSet('jpg')} sizes={sizes} />

      {/* Fallback img element */}
      <img
        src={`${basePath}/${imageName}_1200x800.jpg`}
        alt={alt}
        className={className}
        width={width}
        height={height}
        loading={loading}
        {...(priority && { fetchPriority: 'high' })}
        style={{
          width: width ? `${width}px` : '100%',
          height: height ? `${height}px` : 'auto',
          display: 'block',
          objectFit: 'contain',
        }}
      />
    </picture>
  )
}

export default OptimizedImage
